import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Translator from "../components/Translator";
import PhraseLibrary from "../components/PhraseLibrary";
import Favorites from "../components/Favorites";
import Sessions from "../components/Sessions";
import Patients from "../components/Patients";
import Team from "../components/Team";
import TutorialModal from "../components/TutorialModal";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Languages, LogOut, X, UserCircle2, History, CreditCard, PlayCircle, Inbox, ShieldCheck, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { DISC_FULL } from "../data/disciplines";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [presetText, setPresetText] = useState("");
  const [leftTab, setLeftTab] = useState("phrases");
  const [activePatient, setActivePatient] = useState(null);
  const [patientSessions, setPatientSessions] = useState([]);
  const [topTab, setTopTab] = useState("session");
  const [tutorialOpen, setTutorialOpen] = useState(false);

  const isOrgAdmin = user?.org_role === "admin";

  // Auto-open the tutorial once for first-time users.
  useEffect(() => {
    if (!user?.id) return;
    const key = `tt_tutorial_seen_${user.id}`;
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => setTutorialOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [user?.id]);

  const closeTutorial = () => {
    setTutorialOpen(false);
    if (user?.id) localStorage.setItem(`tt_tutorial_seen_${user.id}`, "1");
  };

  // Load this patient's prior session history when one is opened or when
  // returning to the live session tab (in case new sessions were just logged).
  useEffect(() => {
    if (!activePatient?.id) {
      setPatientSessions([]);
      return;
    }
    if (topTab !== "session") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/patients/${activePatient.id}/sessions`);
        if (!cancelled) setPatientSessions(data || []);
      } catch {
        if (!cancelled) setPatientSessions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activePatient?.id, topTab]);

  const openPatient = async (patient) => {
    setActivePatient(patient);
    setTopTab("session");
    try {
      await api.post(`/patients/${patient.id}/touch`);
    } catch {
      // non-fatal
    }
    toast.success(`Session opened for ${patient.initials}`);
  };

  const clearPatient = () => setActivePatient(null);

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* Top bar */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-900 flex items-center justify-center">
              <Languages className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="font-heading text-lg tracking-tight leading-none">Clinico Translate</p>
              <p className="text-xs text-zinc-500 mt-0.5">{DISC_FULL[user.discipline]}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-zinc-900 leading-none">{user.full_name}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {user.email}
                {user.subscription_status === "trialing" && <span className="ml-2 text-zinc-900 font-medium">· Trial</span>}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setTutorialOpen(true)} data-testid="header-tutorial-button" title="How to use Clinico Translate">
              <PlayCircle className="w-4 h-4 mr-2" /> Watch tutorial
            </Button>
            {user?.is_support_admin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => nav("/support")}
                data-testid="header-support-inbox-button"
                title="Support inbox"
              >
                <Inbox className="w-4 h-4 mr-2" /> Inbox
              </Button>
            )}
            {user?.is_support_admin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => nav("/admin/users")}
                data-testid="header-admin-users-button"
                title="Manage users"
              >
                <UsersIcon className="w-4 h-4 mr-2" /> Users
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => nav("/billing")} data-testid="header-billing-button" title="Billing">
              <CreditCard className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => nav("/account/sessions")} data-testid="header-sessions-button" title="Active sessions">
              <ShieldCheck className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <TutorialModal open={tutorialOpen} onClose={closeTutorial} />

      <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <Tabs value={topTab} onValueChange={setTopTab} className="w-full">
          <TabsList className={`grid w-full md:w-auto md:inline-grid ${isOrgAdmin ? "grid-cols-4" : "grid-cols-3"} h-auto p-1 mb-8`}>
            <TabsTrigger value="session" className="h-11 px-6 data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-session">
              Live session
            </TabsTrigger>
            <TabsTrigger value="patients" className="h-11 px-6 data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-patients">
              Patients
            </TabsTrigger>
            <TabsTrigger value="log" className="h-11 px-6 data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-log">
              Session log
            </TabsTrigger>
            {isOrgAdmin && (
              <TabsTrigger value="team" className="h-11 px-6 data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-team">
                Team
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="session" className="mt-0">
            {/* Active patient banner */}
            {activePatient && (
              <div className="mb-6 border border-zinc-900 rounded-xl bg-zinc-900 text-white p-5" data-testid="active-patient-banner">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0 flex-1">
                    <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                      <UserCircle2 className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">Active patient</p>
                      <p className="font-heading text-2xl tracking-tight leading-none">{activePatient.initials}</p>
                      <p className="text-sm text-zinc-300 mt-1">
                        {activePatient.preferred_language} · {activePatient.discipline || user.discipline}
                      </p>
                      {activePatient.notes && (
                        <p className="text-sm text-zinc-200 mt-3 leading-relaxed max-w-2xl whitespace-pre-wrap">{activePatient.notes}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={clearPatient}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors shrink-0"
                    title="End session"
                    data-testid="active-patient-close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Past sessions for this patient */}
                <div className="mt-5 pt-5 border-t border-white/10" data-testid="patient-history">
                  <div className="flex items-center gap-2 mb-3">
                    <History className="w-3.5 h-3.5 text-zinc-400" />
                    <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">
                      Past sessions {patientSessions.length > 0 && `· ${patientSessions.length}`}
                    </p>
                  </div>
                  {patientSessions.length === 0 ? (
                    <p className="text-sm text-zinc-500" data-testid="patient-history-empty">No prior sessions logged.</p>
                  ) : (
                    <div className="space-y-2 max-h-44 overflow-y-auto pr-2">
                      {patientSessions.slice(0, 5).map((s) => (
                        <div key={s.id} className="text-sm border border-white/10 rounded-lg p-3 bg-white/5" data-testid={`patient-history-${s.id}`}>
                          <p className="text-xs text-zinc-400 mb-1">
                            {new Date(s.created_at).toLocaleString()} · {s.discipline}
                          </p>
                          <p className="text-zinc-100 leading-relaxed line-clamp-2">{s.notes}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Split view */}
            <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
              <aside className="border border-zinc-200 rounded-xl p-5 bg-white h-fit lg:sticky lg:top-24">
                <Tabs value={leftTab} onValueChange={setLeftTab}>
                  <TabsList className="grid w-full grid-cols-2 h-auto p-1 mb-4">
                    <TabsTrigger value="phrases" className="h-10 data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="left-tab-phrases">Library</TabsTrigger>
                    <TabsTrigger value="favorites" className="h-10 data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="left-tab-favorites">Favorites</TabsTrigger>
                  </TabsList>
                  <TabsContent value="phrases">
                    <PhraseLibrary defaultDiscipline={activePatient?.discipline || user.discipline} onUsePhrase={setPresetText} />
                  </TabsContent>
                  <TabsContent value="favorites">
                    <Favorites onUsePhrase={setPresetText} />
                  </TabsContent>
                </Tabs>
              </aside>

              <section>
                <div className="mb-6">
                  <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-2">Live translation</p>
                  <h1 className="font-heading text-4xl md:text-5xl tracking-tight leading-none text-zinc-900">
                    Hello, {user.full_name.split(" ")[0]}.
                  </h1>
                </div>
                <Translator
                  user={user}
                  presetText={presetText}
                  onPresetConsumed={() => setPresetText("")}
                  activePatient={activePatient}
                  onSaveSession={async (notes) => {
                    try {
                      await api.post("/sessions", {
                        patient_initials: activePatient?.initials || "—",
                        patient_language: activePatient?.preferred_language || "Spanish",
                        notes,
                        discipline: activePatient?.discipline || user.discipline,
                        patient_id: activePatient?.id || null,
                      });
                      toast.success("Conversation saved to session log");
                    } catch {
                      toast.error("Could not save conversation");
                    }
                  }}
                />
              </section>
            </div>
          </TabsContent>

          <TabsContent value="patients">
            <Patients user={user} onOpenPatient={openPatient} />
          </TabsContent>

          <TabsContent value="log">
            <Sessions user={user} activePatient={activePatient} />
          </TabsContent>

          {isOrgAdmin && (
            <TabsContent value="team">
              <Team />
            </TabsContent>
          )}
        </Tabs>
      </main>
    </div>
  );
}
